import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useT } from "../i18n";
import { supabase } from "../lib/supabase";
import { signOut } from "../lib/session";
import { loadTimezone } from "../lib/timezones";

const STATUS_KEY = {
  received: "statusReceived",
  in_review: "statusInReview",
  scheduled: "statusScheduled",
} as const;

interface ClientRow {
  name: string | null;
}

interface DemandRow {
  text_content: string | null;
  transcript: string | null;
  status: keyof typeof STATUS_KEY;
}

interface BookingRow {
  id: string;
  start_at: string;
  meet_url: string | null;
}

export function ClientDashboard() {
  const { t, lang } = useT();
  const timezone = loadTimezone();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientRow | null>(null);
  const [demand, setDemand] = useState<DemandRow | null>(null);
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const [{ data: clientRow }, { data: demandRows }, { data: bookingRows }] = await Promise.all([
        supabase.from("clients").select("name").eq("id", user.id).single(),
        supabase
          .from("demands")
          .select("text_content,transcript,status")
          .eq("client_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1),
        supabase
          .from("bookings")
          .select("id,start_at,meet_url")
          .eq("client_id", user.id)
          .order("start_at", { ascending: true }),
      ]);

      if (cancelled) return;
      setClient(clientRow ?? null);
      setDemand(demandRows?.[0] ?? null);
      setBookings(bookingRows ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = async () => {
    await signOut();
    navigate("/login");
  };

  if (loading) return null;

  const nextBooking = bookings[0];

  return (
    <div className="panel wide">
      <span className="eyebrow">{t("dashboardEyebrow")}</span>
      <h2>{client?.name ?? ""}</h2>

      <div className="section-label">{t("latestDemand")}</div>
      <p className="lead">{demand?.text_content ?? demand?.transcript ?? "—"}</p>
      {demand && <span className="ok">{t(STATUS_KEY[demand.status])}</span>}

      <div className="section-label">{t("nextBooking")}</div>
      {nextBooking ? (
        <p>
          {new Intl.DateTimeFormat(lang === "pt" ? "pt-BR" : "en-US", {
            dateStyle: "full",
            timeStyle: "short",
            timeZone: timezone,
          }).format(new Date(nextBooking.start_at))}
          {nextBooking.meet_url && (
            <>
              {" — "}
              <a href={nextBooking.meet_url} target="_blank" rel="noreferrer">
                {t("meetLink")}
              </a>
            </>
          )}
        </p>
      ) : (
        <p className="muted">{t("noNextBooking")}</p>
      )}

      <div className="row">
        <Link className="btn primary btnlink" to="/app/book">
          {t("bookNewTime")}
        </Link>
        <Link className="btn ghost btnlink" to="/app/new-request">
          {t("newDemandCta")}
        </Link>
      </div>

      <div className="section-label">{t("pastBookings")}</div>
      <ul className="events">
        {bookings.map((b) => (
          <li key={b.id}>
            {new Intl.DateTimeFormat(lang === "pt" ? "pt-BR" : "en-US", {
              dateStyle: "short",
              timeStyle: "short",
              timeZone: timezone,
            }).format(new Date(b.start_at))}
          </li>
        ))}
      </ul>

      <div className="row">
        <button className="btn ghost" type="button" onClick={logout}>
          {t("logout")}
        </button>
      </div>
    </div>
  );
}
